import { MESSAGES } from '@guuru/chat-common';
import {
  CHAT_FLOW_STEPS,
  ChatAPI,
  appendLocalMessage,
  replaceLastLocalMessage,
  setBotTyping,
  setChatLoading,
  setCurrentChatSession,
  setInputMode,
  CHAT_INPUT_MODES,
} from '@guuru/chat-web';
import events from '@guuru/events-web';
import { PRIORITIES } from '@guuru/expert-common';

const addQuickAction = function (quickAction) {
  const message = ChatAPI.messages.createAnonymousTextMessage(
    quickAction.name,
    true,
    false,
  );
  ChatAPI.pushPendingMessages(message);
  return message;
};

const addBotRequestQuickActionSelection = function (quickActions) {
  const message = ChatAPI.messages.createBotMessage(
    Date.now(),
    'bot:::requestQuickActionSelection',
    MESSAGES.quickActionSelection,
    { quickActions },
    true,
    false,
  );
  if (
    Object.keys(quickActions).length === 0
    && quickActions.constructor === Object
  ) {
    ChatAPI.pushPendingMessages(message);
  }
  return message;
};

const handleEndFlowWithBotMessage = async (actionCodeMessage, quickAction) => {
  const requestChannelMessage = addBotRequestQuickActionSelection([]);
  replaceLastLocalMessage(requestChannelMessage);

  const message = addQuickAction(quickAction);
  appendLocalMessage(message);

  events.quickActionEndFlow();
  const endFlowMessage = await ChatAPI.addBotMessage(actionCodeMessage);
  appendLocalMessage(endFlowMessage);
};

const handleDirectLink = function (actionCodeMessage) {
  events.quickActionHandleDirectLink();
  const link = document.createElement('a');
  if (actionCodeMessage.startsWith('https://chat.guuru.com')) {
    const locationSearch = window.location.search;
    link.href = `${actionCodeMessage}${locationSearch}`;
  } else {
    link.target = '_blank';
    link.href = actionCodeMessage;
  }
  link.click();
};

const quickActionSelection = {
  validate() {
    const { priority } = ChatAPI.storeRetrieveParams();
    return ![PRIORITIES.bot.value, PRIORITIES.guurusOnly.value].includes(
      priority,
    );
  },

  async render(partnerId) {
    const { quickActions, quickActionsSettings } =
      await ChatAPI.getChatSettings(partnerId);
    events.requestQuickActionSelection();

    const shouldSkipRender =
      quickActions.length === 1 && quickActions[0].action === 'requestQuestion';

    if (shouldSkipRender) {
      const quickAction = quickActions[0];
      if (quickAction?.assignTo) {
        ChatAPI.storeChatPriority(
          quickAction?.expertsPriority || quickAction.assignTo,
        );
      }
      const welcomeMessage = ChatAPI.messages.craeteBotMessage(
        Date.now(),
        quickAction.response,
      );

      return { goToNextStep: true, nextStepOptions: { welcomeMessage } };
    }

    if (quickActionsSettings?.quickActionSkipIfAdviceQuestion) {
      setInputMode(CHAT_INPUT_MODES.restart);
    }

    const requestQuickActionSelection =
      addBotRequestQuickActionSelection(quickActions);
    appendLocalMessage(requestQuickActionSelection);
    setChatLoading(false);

    return { goToNextStep: false };
  },

  async processResponse(partnerId, { quickAction, createChatImmediately }) {
    setBotTyping(false);

    events.quickActionSubmitted(
      `Quick Action: ${quickAction.id} (partnerId: ${partnerId}; name: ${quickAction.name})`,
      quickAction,
    );

    const actionCodeMessage = quickAction.response;
    switch (quickAction.action) {
      case 'endFlowWithBotMessage': {
        await handleEndFlowWithBotMessage(actionCodeMessage, quickAction);
        return { goToStep: CHAT_FLOW_STEPS.chatEnd };
      }
      case 'requestQuestion': {
        if (quickAction?.assignTo) {
          ChatAPI.storeChatPriority(
            quickAction?.expertsPriority || quickAction.assignTo,
          );
        }

        events.quickActionRequestQuestion();
        const qaGroup = ChatAPI.messages.craeteBotMessage(
          Date.now(),
          'bot:::requestQuickActionSelectionGuurus',
        );
        const qaText = ChatAPI.messages.createAnonymousTextMessage(
          quickAction.name,
          true,
          false,
        );
        ChatAPI.pushPendingMessages(qaGroup);
        ChatAPI.pushPendingMessages(qaText);

        if (createChatImmediately) {
          replaceLastLocalMessage(qaGroup);
          appendLocalMessage(qaText);
          return { goToNextStep: true };
        }

        setCurrentChatSession({ messages: [qaGroup, qaText] });

        const welcomeMessage = ChatAPI.messages.craeteBotMessage(
          Date.now(),
          actionCodeMessage,
        );

        return { nextStepOptions: { welcomeMessage } };
      }
      case 'directLink': {
        handleDirectLink(actionCodeMessage);
        return { stayInCurrentStep: true };
      }
      default:
    }
    return {};
  },
};

export default quickActionSelection;
